/*
 * OrbitHandoff — Stage 2 outcome `planner_direct`.
 *
 * Triggered when a user picked Custom in Stage 1 AND clarity hit the
 * top threshold (≥ 0.975 — exceptional acceptance criteria already in
 * L1.E). They're skipping L2 entirely. The auto-generated multi-phase
 * PRD is drafted from L1 directly; this surface is the "look, accept,
 * ship" page.
 *
 * Flow:
 *   1. Fetch engagement via /api/intake/engagement/:eid
 *   2. Render the L1 PRD seed (problem · acceptance · target outcome)
 *      with phases visible
 *   3. CTA "Hand off to engineering" → POST /api/agents/projects with
 *      the engagement_id + tier → validx creates the project →
 *      land on the agents list (rows open the validx builder via the bridge)
 *
 * Cost shape: burns 1 agent_request. The quota was already verified at
 * /stage1/pick (Phase 2 guard); this page proceeds assuming headroom.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Rocket, ChevronLeft, ChevronRight, Loader2, CheckCircle2, Layers,
} from 'lucide-react';


const API_BASE = process.env.REACT_APP_API_URL ?? '';


export default function OrbitHandoff() {
  const navigate = useNavigate();
  const { engagementId } = useParams<{ engagementId: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [handing, setHanding] = useState(false);
  const [handErr, setHandErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      if (!engagementId) return;
      try {
        const res = await axios.get(`${API_BASE}/api/intake/engagement/${engagementId}`);
        if (!cancelled) setData(res.data);
      } catch (err: any) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setError(typeof detail === 'string' ? detail : 'Could not load engagement.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [engagementId]);

  const handleHandoff = async () => {
    if (!data || handing) return;
    setHanding(true);
    setHandErr(null);
    try {
      const summary = data.l1_summary || {};
      const title = (summary.company_name
        ? `${summary.company_name} — `
        : '') + (
          summary.problem_summary
            ? summary.problem_summary.slice(0, 60)
            : 'Agent build'
        );
      const description = summary.acceptance_criteria
        || summary.problem_summary
        || 'Direct planner handoff from Orbit intake.';
      const res = await axios.post(`${API_BASE}/api/agents/projects`, {
        title,
        description,
        type: 'FEATURE',
        priority: data.tier_at_decision === 'scale' || data.tier_at_decision === 'team'
          ? 'HIGH'
          : 'NORMAL',
        orbitEngagementId: data.engagement_id,
      });
      // Workspace lives on validx now — land on the agents list (its rows
      // open the validx builder via the HMAC bridge) instead of the retired
      // in-app /agents/[id] viewer.
      navigate('/agents');
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setHandErr(typeof detail === 'string' ? detail : 'Could not hand off. Try again.');
      setHanding(false);
    }
  };

  return (
    <div style={shellStyles.page}>
      <div style={shellStyles.card}>
        <button
          type="button"
          onClick={() => navigate('/my-business#routing')}
          style={shellStyles.backLink}
        >
          <ChevronLeft size={14} strokeWidth={2} />
          Back to routing
        </button>

        <div style={{ ...shellStyles.iconCircle, background: 'rgba(99,102,241,0.14)', color: '#3b3897' }}>
          <Rocket size={26} strokeWidth={2} />
        </div>

        <div style={shellStyles.eyebrow}>Stage 2 · Planner direct</div>

        {loading ? (
          <p style={shellStyles.bodyText}>
            <Loader2 size={14} className="animate-spin" style={{ marginRight: 6 }} />
            Reading your engagement…
          </p>
        ) : error ? (
          <>
            <h1 style={shellStyles.title}>Couldn't load this engagement.</h1>
            <p style={shellStyles.bodyText}>{error}</p>
          </>
        ) : data ? (
          <>
            <h1 style={shellStyles.title}>
              Your PRD is{' '}
              <em style={{ fontFamily: "var(--font-serif, 'IBM Plex Serif', serif)" }}>
                ready
              </em>.
            </h1>

            <p style={shellStyles.bodyText}>
              You scoped this sharply. Skipping L2 — the engineering planner reads
              your L1 dossier directly and starts shipping in phases.
            </p>

            {/* PRD seed preview */}
            <div style={shellStyles.summaryBlock}>
              <div style={shellStyles.summaryEyebrow}>The PRD seed</div>
              <dl style={shellStyles.dl}>
                {data.l1_summary?.problem_summary && (
                  <>
                    <dt style={shellStyles.dt}>Problem</dt>
                    <dd style={shellStyles.dd}>{data.l1_summary.problem_summary}</dd>
                  </>
                )}
                {data.l1_summary?.acceptance_criteria && (
                  <>
                    <dt style={shellStyles.dt}>Acceptance criteria</dt>
                    <dd style={shellStyles.dd}>{data.l1_summary.acceptance_criteria}</dd>
                  </>
                )}
                {data.l1_summary?.urgency && (
                  <>
                    <dt style={shellStyles.dt}>Urgency</dt>
                    <dd style={shellStyles.dd}>{data.l1_summary.urgency}</dd>
                  </>
                )}
              </dl>
            </div>

            {/* Phase preview */}
            <div style={shellStyles.phaseRow}>
              <Layers size={12} strokeWidth={2.25} style={{ marginRight: 6, verticalAlign: '-1px' }} />
              <span>Multi-phase build. Phase 1 ships first; subsequent phases pick up as Phase 1 lands.</span>
            </div>

            <p style={shellStyles.note}>
              <CheckCircle2 size={12} style={{ marginRight: 6, verticalAlign: '-1px' }} strokeWidth={2.5} />
              Burns 1 agent build from your {data.tier_at_decision || 'free'} plan.
              {(data.tier_at_decision === 'team' || data.tier_at_decision === 'scale') &&
                ' Priority queue active.'}
            </p>

            {handErr && (
              <p style={shellStyles.errorText}>{handErr}</p>
            )}

            <div style={shellStyles.actions}>
              <button
                type="button"
                onClick={handleHandoff}
                disabled={handing}
                style={shellStyles.primaryCta}
              >
                {handing
                  ? <Loader2 size={14} className="animate-spin" />
                  : <Rocket size={14} strokeWidth={2.25} />}
                <span>{handing ? 'Handing off…' : 'Hand off to engineering'}</span>
                <ChevronRight size={14} strokeWidth={2.5} />
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}


const shellStyles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f8f9fb',
    padding: 24,
    fontFamily: "var(--font-sans, 'IBM Plex Sans', system-ui, sans-serif)",
  },
  card: {
    maxWidth: 620,
    width: '100%',
    background: '#fff',
    border: '1px solid #e2e5eb',
    borderRadius: 16,
    padding: '40px 40px 32px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'transparent',
    border: 0,
    color: '#545454',
    fontSize: 12,
    cursor: 'pointer',
    padding: 0,
    marginBottom: 20,
  },
  iconCircle: {
    width: 56,
    height: 56,
    borderRadius: 14,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 18,
  },
  eyebrow: {
    fontFamily: "var(--font-sans, 'IBM Plex Sans', system-ui, sans-serif)",
    fontSize: 10.5,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#3b3897',
    lineHeight: 1.3,
    marginBottom: 10,
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    letterSpacing: '-0.015em',
    color: '#000',
    margin: '0 0 12px',
    lineHeight: 1.25,
  },
  bodyText: {
    fontSize: 14.5,
    color: '#545454',
    lineHeight: 1.55,
    margin: '0 0 20px',
  },
  summaryBlock: {
    background: '#f7f9fc',
    border: '1px solid #e2e5eb',
    borderRadius: 12,
    padding: '14px 16px',
    marginBottom: 20,
  },
  summaryEyebrow: {
    fontFamily: "var(--font-sans, 'IBM Plex Sans', system-ui, sans-serif)",
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#3b3897',
    marginBottom: 10,
  },
  dl: { margin: 0 },
  dt: {
    fontSize: 11,
    fontWeight: 600,
    color: '#545454',
    marginTop: 8,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontFamily: "var(--font-sans, 'IBM Plex Sans', system-ui, sans-serif)",
  },
  dd: {
    margin: '4px 0 0',
    fontSize: 13.5,
    color: '#000',
    lineHeight: 1.55,
  },
  phaseRow: {
    fontSize: 12,
    color: '#545454',
    marginBottom: 16,
  },
  note: {
    fontSize: 12,
    color: '#545454',
    background: '#f7f9fc',
    border: '1px solid #e2e5eb',
    borderRadius: 8,
    padding: '10px 12px',
    marginBottom: 20,
    lineHeight: 1.55,
  },
  errorText: {
    fontSize: 12,
    color: '#fd2751',
    margin: '0 0 12px',
  },
  actions: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
  },
  primaryCta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '11px 18px',
    background: '#18191d',
    color: '#fff',
    border: 0,
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};
